import { useLingui } from '@lingui/react/macro';
import { getTimesheetPeriod } from '@sit/core';
import { usePostCrewTimesheet } from '@sit/openapi';
import { useGetCurrentCompany } from '@web/api/company/use-get-company';
import { usePostTimesheet } from '@web/api/timesheet/use-post-timesheet';
import { useGetUser } from '@web/api/users/use-get-user';
import { Navigate, useNavigate } from 'react-router';
import { useLocationQuery } from '../../../helpers/hooks';
import toastr from '../../../helpers/toastr';
import { crewTimesheetIndexRoute, getCrewTimesheetRoute, getStaffTimesheetsRoute, getTimesheetsRoute } from '../helpers/routes';
import { useIsStaff, useTimesheetModalActions } from '../hooks';
import type { TimesheetFormData } from './create-timesheet-modal-types';
import { CreateCrewTimesheetModal, CreateTimesheetModal } from './CreateTimesheetModal';
import {
  mustHaveCreateStaffPermissions,
  mustHaveEditStaffPermissions,
  mustHaveEmployee,
  mustHaveEmployeeGroup,
  noEditPermissions,
} from './messages';

const defaultData = {
  weekStart: 1,
  timesheetDuration: 7,
} as const;

const useDefaultData = (query: URLSearchParams) => {
  const { data: company } = useGetCurrentCompany();
  const { weekStart, timesheetDuration } = company || defaultData;

  const queryStartDate = query.get('start_date');
  const queryEndDate = query.get('end_date');

  if (queryStartDate && queryEndDate) {
    return {
      startDate: queryStartDate,
      endDate: queryEndDate,
    };
  }
  return getTimesheetPeriod(timesheetDuration, weekStart);
};

interface NewTimesheetProps {
  open: boolean;
}

export function NewTimesheet({ open }: NewTimesheetProps) {
  const { i18n } = useLingui();
  const query = useLocationQuery();
  const { startDate, endDate } = useDefaultData(query);
  const { data: userData } = useGetUser();
  const externalEmployeeId = userData?.employee?.employeeId;
  const { closeModal } = useTimesheetModalActions();
  const staff = useIsStaff();
  const { data: company } = useGetCurrentCompany();

  const { mutate: createTimesheet, isPending: isSubmitting } = usePostTimesheet();

  const handleSubmit = ({ startDate: submittedStartDate, endDate: submittedEndDate, description, employeeId }: TimesheetFormData) => {
    if (!company) return;
    if (!employeeId) {
      toastr.warning(i18n._(mustHaveEmployee));
      return;
    }
    createTimesheet({
      body: {
        description,
        endDate: submittedEndDate,
        startDate: submittedStartDate,
        externalEmployeeId: employeeId,
      },
      queryParams: {
        companyId: company.id,
        staff: staff ? 'true' : 'false',
      },
    });
  };

  if (open && !staff && userData && (!userData.canEditTimesheets || !userData.canCreateTimesheet)) {
    const messageDescriptor = !userData.canEditTimesheets ? noEditPermissions : mustHaveEmployee;
    toastr.warning(i18n._(messageDescriptor));
    return <Navigate to={getTimesheetsRoute} />;
  }
  if (open && staff && userData && (!userData.canEditStaffTimesheets || !userData.canCreateStaffTimesheets)) {
    const messageDescriptor = !userData.canEditStaffTimesheets ? mustHaveEditStaffPermissions : mustHaveCreateStaffPermissions;
    toastr.warning(i18n._(messageDescriptor));
    return <Navigate to={getStaffTimesheetsRoute} />;
  }
  return (
    <CreateTimesheetModal
      staff={staff}
      data={{
        startDate,
        endDate,
        description: '',
        employeeId: externalEmployeeId ?? undefined,
      }}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit}
      onClose={closeModal}
      open={open}
    />
  );
}

export function NewCrewTimesheet({ open }: NewTimesheetProps) {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const query = useLocationQuery();
  const { startDate, endDate } = useDefaultData(query);
  const { data: userData } = useGetUser();
  const { closeModal } = useTimesheetModalActions();
  const { data: company } = useGetCurrentCompany();
  const { mutate: createCrewTimesheet, isPending } = usePostCrewTimesheet();

  const handleSubmit = ({ startDate: submittedStartDate, endDate: submittedEndDate, description, employeeGroupId }: TimesheetFormData) => {
    if (!company) return;
    if (!employeeGroupId) {
      toastr.warning(i18n._(mustHaveEmployeeGroup));
      return;
    }

    createCrewTimesheet(
      {
        body: {
          description,
          endDate: submittedEndDate,
          startDate: submittedStartDate,
          employeeGroupId,
        },
        queryParams: { companyId: company.id },
      },
      {
        onSuccess: (response) => {
          closeModal();
          void navigate(getCrewTimesheetRoute(response.id));
        },
      },
    );
  };

  if (open && userData && (!userData.canEditStaffTimesheets || !userData.canCreateStaffTimesheets)) {
    const messageDescriptor = !userData.canEditStaffTimesheets ? mustHaveEditStaffPermissions : mustHaveCreateStaffPermissions;
    toastr.warning(i18n._(messageDescriptor));
    return <Navigate to={crewTimesheetIndexRoute} />;
  }

  return (
    <CreateCrewTimesheetModal
      data={{ startDate, endDate, description: '' }}
      isSubmitting={isPending}
      onSubmit={handleSubmit}
      onClose={closeModal}
      open={open}
    />
  );
}
